import React, { useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { showAlert } from 'actions/app';
import { executeRestoreSearchProjectsService } from 'services/searchProjects';
import { getAllSearchProjectsFolders } from 'services/searchProjects/folders';
import strings from 'strings';
import TextBox from 'UI/components/atoms/TextBox';
import { When } from 'UI/components/atoms/When';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import NumberedForm from 'UI/components/molecules/NumberedForm';
import TreeViewSelector from 'UI/components/molecules/TreeViewSelector/TreeViewSelector';
import { buildCloseAndSaveButtonDefinition } from 'UI/components/templates/FPDrawer';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import {
  DEFAULT_SEARCH_PROJECT_FOLDERS_EXCLUDE_IDS,
  SEARCH_PROJECT_ROOT_FOLDERS
} from 'UI/constants/defaults';

const FORM_FIELDS_MAP = {
  parentFolder: {
    key: 'parentFolder'
  }
};

const { restoreSearchProject: formCopies } = strings.searchProjects.forms;

/**
 * @param {Object} props
 * @param {Function} props.onClose
 * @param {Function} props.onRestore
 * @param {Array} props.searchProjects
 */
const RestoreSearchProjectDrawer = ({ onClose, onRestore, searchProjects }) => {
  const dispatch = useDispatch();
  const [uiState, setUiState] = useState({
    isSaving: false,
    isSuccess: false
  });

  const form = useForm({
    defaultValues: { [FORM_FIELDS_MAP.parentFolder.key]: SEARCH_PROJECT_ROOT_FOLDERS.mine }
  });
  const { handleSubmit } = form;

  const shouldRestoreMultipleSearchProjects = searchProjects.length > 1;
  const finalTitle = strings.formatString(formCopies.title, {
    subject: shouldRestoreMultipleSearchProjects
      ? formCopies.subject.plural
      : formCopies.subject.singular
  });

  const onSubmit = handleSubmit(async formData => {
    setUiState(prev => ({ ...prev, isSaving: true }));
    const folderId = formData[FORM_FIELDS_MAP.parentFolder.key].id;
    const { alert, success } = await executeRestoreSearchProjectsService(
      shouldRestoreMultipleSearchProjects,
      { searchProjects, folderId }
    );
    dispatch(showAlert(alert));
    setUiState({ isSaving: false, isSuccess: success });

    if (success) {
      onRestore && onRestore();
      onClose();
    }
  });

  const buttonDefinitions = buildCloseAndSaveButtonDefinition({
    onClose,
    isSaveDisabled: uiState.isSaving,
    isSaving: uiState.isSaving
  });

  return (
    <>
      <FPDrawer
        FPDrawerProps={{ open: true }}
        CardHeaderProps={{ title: finalTitle }}
        onClose={onClose}
        ContentWrapper="form"
        ContentWrapperProps={{ onSubmit, id: 'drawer-form' }}
        footer={
          <FPButtonBuilder
            GridContainerProps={{ spacing: 2 }}
            buttonDefinitions={buttonDefinitions}
          />
        }
      >
        <FormContext {...form}>
          <NumberedForm>
            <When condition={!shouldRestoreMultipleSearchProjects}>
              <NumberedForm.Item title={formCopies.sections.name.title}>
                <TextBox value={searchProjects[0]?.name} disabled />
              </NumberedForm.Item>
            </When>
            <NumberedForm.Item title={formCopies.sections.parentFolder.title}>
              <TreeViewSelector
                shouldRegisterWithin
                formFieldName={FORM_FIELDS_MAP.parentFolder.key}
                placeholder={formCopies.sections.parentFolder.placeholder}
                TreeViewLoaderProps={{
                  enableIconClick: true,
                  onlyShowFolders: true,
                  service: {
                    method: getAllSearchProjectsFolders,
                    queryParams: { excludeIds: DEFAULT_SEARCH_PROJECT_FOLDERS_EXCLUDE_IDS }
                  }
                }}
              />
            </NumberedForm.Item>
          </NumberedForm>
        </FormContext>
      </FPDrawer>
    </>
  );
};

export default RestoreSearchProjectDrawer;
